import React, { useState } from "react";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { useNavigate } from "react-router-dom";
import { loginUser, verifyTwoFACode } from "../utilities/api";
import Cookies from "js-cookie";

const LoginRegistration = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [show2FA, setShow2FA] = useState(false);
    const [twoFACode, setTwoFACode] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    // Validate email format
    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        setIsEmailValid(validateEmail(e.target.value));
    };
    const handleLogin = async () => {
        if (isEmailValid) {
            setLoading(true);
            setErrorMessage("");
            try {
                const loginResponse = await loginUser(email);
                if (loginResponse?.requires2FA) {
                    setShow2FA(true);
                } else {
                    setErrorMessage(" Unexpected error. Please try again.");
                }
            } catch (error) {
                setErrorMessage(error.message || " Login failed. Please try again.");
            } finally {
                setLoading(false);
            }
        }
    };
    const handle2FAChange = (e) => {
        setTwoFACode(e.target.value);
    };
    const handle2FASubmit = async () => {
        setLoading(true);
        setErrorMessage("");
        try {
            console.log(` Debug: Sending email=${email}, 2FA Code=${twoFACode}`);
            const verifyResponse = await verifyTwoFACode(email, twoFACode);
            if (verifyResponse?.success) {
                console.log("2FA Verification Successful.");
                
                // Redirect to `/` (which loads <Layout /> and triggers <PageHome />)
                setTimeout(() => navigate("/", { replace: true }), 200);
            } else {
                setErrorMessage("2FA code is invalid. Please enter a valid 2FA code.");
            }
        } catch (error) {
            let errorMsg = "2FA verification failed. Please try again.";

            if (error.response?.data?.error?.message) {
                const apiErrorMsg = error.response.data.error.message.toLowerCase();

                if (apiErrorMsg.includes("invalid")) {
                    errorMsg = "The 2FA code you entered is incorrect. Please check and try again.";
                } else if (apiErrorMsg.includes("expired")) {
                    errorMsg = "The 2FA code has expired. Please request a new one.";
                }
            }
            setErrorMessage(errorMsg);
        } finally {
            setLoading(false);
        }
    };
    return (
        <div>
            <div className="p-3 bg-gray-900">
                <div className="text-center">
                    <img src="/media/images/benlogo.svg" alt="Benedictus App" className="w-[198px] h-[50px] mx-auto" />
                </div>
            </div>
            <div className="mt-6 p-4 sm:p-6 bg-gray-0 max-w-full sm:max-w-[588px] mx-auto responsive-mobile flex flex-col sm:flex-col justify-between sm:justify-center rounded-t-xl">
                <div className="flex items-center mb-6">
                    <h1 className="text-2xl font-bold leading-7 text-gray-900 md:text-2xl xl:text-3xl">
                        Login & Registration
                    </h1>
                </div>
                <h5 className="mb-5 text-base font-bold leading-5 text-gray-900">
                    {show2FA ? "2FA Verification" : "Login"}
                </h5>

                {!show2FA ? (
                    <div>
                        <div className="relative mb-4">
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={handleEmailChange}
                                className="w-full px-3 py-2 mt-2 border rounded-lg"
                                placeholder="Enter your email"
                            />
                            {!isEmailValid && email.length > 0 && (
                                <p className="mt-1 text-sm text-red-500">Invalid email address</p>
                            )}
                        </div>
                        {errorMessage && <p className="text-sm text-center text-red-500">{errorMessage}</p>}
                        <div className="flex justify-center mt-12">
                            <Button
                                onClick={handleLogin}
                                disabled={!isEmailValid || loading}
                                className={`w-full sm:w-1/2 text-white-50 text-base leading-5 font-semibold font-redhat rounded-full transition-colors duration-300 
                                    ${isEmailValid ? "bg-gold-300 hover:bg-gold-400" : "bg-gray-400 hover:bg-gray-500"}
                                    `}
                            >
                                {loading ? "Logging in..." : "Login"}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <p className="mb-4 text-sm text-green-600">
                            The 2FA code was sent successfully to your email. Please check your inbox or spam folder if you
                            don't see it.
                        </p>
                        <div className="relative mb-4">
                            <Label htmlFor="2fa">Enter 2FA Code</Label>
                            <Input
                                id="2fa"
                                type="text"
                                value={twoFACode}
                                onChange={handle2FAChange}
                                className={`w-full border rounded-lg px-3 py-2 mt-2 ${
                                    errorMessage ? "border-red-500" : ""
                                }`}
                                placeholder="Enter 2FA code"
                            />
                            {errorMessage && <p className="mt-1 text-sm text-red-500">{errorMessage}</p>}
                        </div>
                        <div className="flex justify-center mt-12">
                            <Button
                                onClick={handle2FASubmit}
                                disabled={twoFACode.length === 0 || loading}
                                className={`w-full sm:w-1/2 text-white-50 text-base leading-5 font-semibold font-redhat rounded-full transition-colors duration-300 
                                    ${twoFACode.length > 0 ? "bg-gold-300 hover:bg-gold-400" : "bg-gray-400 hover:bg-gray-500"}
                                    `}
                            >
                                {loading ? "Verifying..." : "Submit"}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LoginRegistration;
